import { Box, Flex, Text } from '@chakra-ui/react';
import { OnboardingTaglineBadge } from '@/pages/onboarding/_components/panel/OnboardingTaglineBadge';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const SLIDE_DURATION_MS = 5200;

interface NewsItem {
  titleKey: string;
  descKey: string;
  tagKey: string;
}

const NEWS_ITEMS: NewsItem[] = [
  {
    titleKey: 'authPage.news.companion.title',
    descKey: 'authPage.news.companion.desc',
    tagKey: 'authPage.news.companion.tag',
  },
  {
    titleKey: 'authPage.news.mcp.title',
    descKey: 'authPage.news.mcp.desc',
    tagKey: 'authPage.news.mcp.tag',
  },
  {
    titleKey: 'authPage.news.listening.title',
    descKey: 'authPage.news.listening.desc',
    tagKey: 'authPage.news.listening.tag',
  },
  {
    titleKey: 'authPage.news.partnershipAds.title',
    descKey: 'authPage.news.partnershipAds.desc',
    tagKey: 'authPage.news.partnershipAds.tag',
  },
];

/**
 * Auto-advancing "what's new" carousel for the auth brand panel. The active
 * progress dot drives the timing: its fill animates over SLIDE_DURATION_MS and
 * advances to the next slide on finish (mirrors the design). Hovering the card
 * pauses the running animation; leaving resumes it.
 */
interface AuthNewsCarouselProps {
  onSlideChange?: (index: number) => void;
  accentColor?: string;
}

export const AuthNewsCarousel = ({
  onSlideChange,
  accentColor = 'white',
}: AuthNewsCarouselProps = {}) => {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);
  const animationRef = useRef<Animation | null>(null);
  const pausedRef = useRef(false);

  const goTo = useCallback(
    (next: number) => {
      const normalized = ((next % NEWS_ITEMS.length) + NEWS_ITEMS.length) % NEWS_ITEMS.length;
      setIdx(normalized);
      onSlideChange?.(normalized);
    },
    [onSlideChange]
  );

  // Ref callback on the active dot's fill: starts the progress animation and
  // advances on finish. Re-runs whenever the active dot remounts (idx change).
  const attachFill = useCallback(
    (el: HTMLElement | null) => {
      if (!el) return;
      animationRef.current?.cancel();
      const animation = el.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], {
        duration: SLIDE_DURATION_MS,
        easing: 'linear',
        fill: 'forwards',
      });
      animationRef.current = animation;
      if (pausedRef.current) animation.pause();
      animation.onfinish = () => goTo(idx + 1);
    },
    [idx, goTo]
  );

  const pause = useCallback(() => {
    pausedRef.current = true;
    animationRef.current?.pause();
  }, []);

  const resume = useCallback(() => {
    pausedRef.current = false;
    if (animationRef.current && animationRef.current.playState !== 'finished') {
      animationRef.current.play();
    }
  }, []);

  return (
    <Box
      onMouseEnter={pause}
      onMouseLeave={resume}
      bg="white"
      borderWidth="1px"
      borderColor="primary.900/10"
      borderRadius="18px"
      px="24px"
      pt="22px"
      pb="20px"
    >
      <Box mb="16px">
        <OnboardingTaglineBadge color={accentColor}>{t('authPage.news.label')}</OnboardingTaglineBadge>
      </Box>

      <Box overflow="hidden">
        <Flex
          transition="transform .55s cubic-bezier(.22,1,.36,1)"
          transform={`translateX(-${idx * 100}%)`}
        >
          {NEWS_ITEMS.map((item) => (
            <Box key={item.titleKey} flex="0 0 100%" minW={0} minH="124px" pr="2px">
              <Text
                fontFamily="heading"
                fontSize="19px"
                fontWeight="600"
                letterSpacing="-0.015em"
                lineHeight="1.3"
                color="primary.900"
              >
                {t(item.titleKey)}
              </Text>
              <Text
                fontFamily="body"
                fontSize="14px"
                lineHeight="1.55"
                fontWeight="400"
                color="gray.600"
                mt="9px"
              >
                {t(item.descKey)}
              </Text>
              <Text
                fontFamily="heading"
                fontSize="12px"
                fontWeight="500"
                color="gray.500"
                mt="14px"
              >
                {t(item.tagKey)}
              </Text>
            </Box>
          ))}
        </Flex>
      </Box>

      <Flex align="center" gap="7px" mt="18px">
        {NEWS_ITEMS.map((item, i) => {
          const active = i === idx;
          return (
            <button
              type="button"
              key={item.titleKey}
              onClick={() => goTo(i)}
              aria-label={t('authPage.news.goTo')}
              style={{
                position: 'relative',
                overflow: 'hidden',
                height: '6px',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                borderRadius: '9999px',
                transition: 'width .45s cubic-bezier(.22,1,.36,1), background-color .3s',
                width: active ? '26px' : '6px',
                background: active
                  ? 'var(--chakra-colors-gray-200)'
                  : 'var(--chakra-colors-gray-300)',
              }}
            >
              {active && (
                <span
                  ref={attachFill}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'block',
                    borderRadius: '9999px',
                    background: 'var(--chakra-colors-primary-900)',
                    transformOrigin: 'left center',
                    transform: 'scaleX(0)',
                  }}
                />
              )}
            </button>
          );
        })}
      </Flex>
    </Box>
  );
};
